import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Button from "../CustomButton/Button";
import ToastNotification from "../Notification/ToastNotification";
import { clearNotification } from "../../reduser/notificationSlice";
import { showNotification } from "../../utils/notificationUtils";
import axiosInstance from "../../utils/axiosInstanse";
import host from "../../host";
import Spiner from "../Spiner/Spiner";
import "./PassApplication.css";

registerLocale("ru", ru);
setDefaultLocale("ru");

const PassApplicationForm = () => {
  const dispatch = useDispatch();
  const notification = useSelector((state) => state.notification);

  // === Состояния для выпадающих списков ===
  const [visitPlaces, setVisitPlaces] = useState([]);
  const [visitPurposes, setVisitPurposes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [hasAccess, setHasAccess] = useState(null);

  // === Состояния для автодополнения фамилии ===
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [lastNameInput, setLastNameInput] = useState("");

  // === Состояния для организации ===
  const [organizationResults, setOrganizationResults] = useState([]);
  const [showOrganization, setShowOrganization] = useState(false);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [currentGuestId, setCurrentGuestId] = useState(null);

  // === Состояния для поиска организации по названию ===
  const [orgInputValue, setOrgInputValue] = useState("");
  const [selectedOrgItem, setSelectedOrgItem] = useState(null); // { name } или null

  // === Состояние для дополнительных сотрудников ===
  const [additionalEmployees, setAdditionalEmployees] = useState([
    { lastName: "", firstName: "", middleName: "", comment: "" },
  ]);

  // === Состояния для автодополнения у дополнительных сотрудников ===
  // { [index]: { results, show, loading, query } }
  const [empAutocomplete, setEmpAutocomplete] = useState({});
  const empTimerRef = useRef({}); // ref для debounce таймеров по индексу

  // === Состояние для машин ===
  const [vehicles, setVehicles] = useState([
    {
      vehicleNumber: "",
      driverName: "",
      vehicleModel: "",
      vehicleColor: "",
      vehicleNotes: "",
    },
  ]);
  const vehicleErrors = useRef({});

  // === Ссылки на поля ===
  const lastNameRef = useRef(null);
  const comeToRef = useRef(null);
  const organizationRef = useRef(null);

  // === Вспомогательные функции ===
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const convertToISO = (ddmmyyyy) => {
    if (!ddmmyyyy || !ddmmyyyy.includes(".")) return "";
    const [day, month, year] = ddmmyyyy.split(".");
    return `${year}-${month}-${day}`;
  };

  // find_guests возвращает { ID, APPL_ID, NAME, BIRTHDAY, ORGANIZATION }
  const normalizeGuest = (g) => {
    const name = g.NAME || "";
    const [lastName = "", firstName = "", middleName = ""] = name
      .trim()
      .split(/\s+/);
    return {
      id: g.ID,
      lastName,
      firstName,
      middleName,
      birthday: g.BIRTHDAY || null,
      organization: g.ORGANIZATION || "",
      fullName: name,
    };
  };

  const getVisitorFullName = (emp) =>
    [emp.lastName, emp.firstName, emp.middleName]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(" ");

  const today = new Date();
  const todayFormatted = formatDate(today);

  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    visitPlace: "",
    comeTo: "",
    phone: "",
    purpose: "",
    passType: "single",
    birthDate: null,
    passPeriod: `${todayFormatted} - ${todayFormatted}`,
    visitTime: "07:30 - 16:00",
    weekendVisit: false,
    safetyBriefing: false,
    vehicleAccess: false,
    generalNotes: "",
    organization: "",
    owner: "",
  });

  // === Валидационные функции ===
  const validateName = (value, fieldName) => {
    if (!value) return "";

    if (!/^[a-zA-Zа-яА-ЯёЁ\s\-]+$/.test(value)) {
      return "Допустимы только буквы, пробелы и дефисы";
    }

    if (value.length > 100) {
      return `Максимальная длина - 100 символов`;
    }

    return "";
  };

  const validatePhone = (value) => {
    if (!value) return "";

    if (!/^\+\d+$/.test(value)) {
      return "Телефон должен начинаться с + и содержать только цифры";
    }

    if (value.length < 4 || value.length > 16) {
      return "Некорректная длина номера телефона";
    }

    return "";
  };

  const validateBirthDate = (date) => {
    if (!date) return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return "Дата рождения не может быть в будущем";
    }

    const minDate = new Date("1900-01-01");
    if (selectedDate < minDate) {
      return "Некорректная дата рождения";
    }

    return "";
  };

  const validatePassDate = (
    date,
    isStartDate = true,
    currentStartDate = null,
  ) => {
    if (!date) return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (isStartDate && selectedDate < today) {
      return "Дата начала пропуска не может быть в прошлом";
    }

    if (!isStartDate && currentStartDate) {
      const startDate = new Date(currentStartDate);
      startDate.setHours(0, 0, 0, 0);

      if (selectedDate < startDate) {
        return "Дата окончания не может быть раньше даты начала";
      }

      if (selectedDate < today) {
        return "Дата окончания пропуска не может быть в прошлом";
      }
    }

    return "";
  };

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [placesResponse, purposesResponse] = await Promise.all([
          axiosInstance.get(`${host}api/v2/get_visit_places/`),
          axiosInstance.get(`${host}api/v2/get_visit_purposes/`),
        ]);

        if (placesResponse.data && purposesResponse.data) {
          setVisitPlaces(placesResponse.data);
          setVisitPurposes(purposesResponse.data);
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        if (error.response?.status === 403 || error.response?.status === 401) {
          setHasAccess(false);
        } else {
          setHasAccess(false);
        }
      } finally {
        setFormLoading(false);
      }
    };

    fetchFormData();
  }, [dispatch]);

  // === Дебаунсинг поиска ===
  useEffect(() => {
    if (lastNameInput.length >= 3) {
      setLoadingAutocomplete(true);
      const timer = setTimeout(() => {
        searchGuestsByLastName(lastNameInput);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowAutocomplete(false);
      setAutocompleteResults([]);
    }
  }, [lastNameInput]);

  // === Дебаунсинг поиска организации ===
  useEffect(() => {
    // Не запускать поиск, если организация уже выбрана
    if (selectedOrgItem) return;

    if (orgInputValue.length >= 3) {
      setLoadingOrganization(true);
      setShowOrganization(true);
      const timer = setTimeout(() => {
        searchOrganizations(orgInputValue);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowOrganization(false);
      setOrganizationResults([]);
      // Очищаем выбранную организацию при полной очистке поля
      if (orgInputValue === "") {
        setSelectedOrgItem(null);
        setSelectedOrganization("");
        setSelectedOrganizationId(null);
        setFormData((prev) => ({ ...prev, organization: "" }));
      }
    }
  }, [orgInputValue, selectedOrgItem]);

  // === Авто-подстановка водителя: одна машина + один посетитель ===
  useEffect(() => {
    if (!formData.vehicleAccess) return;
    if (vehicles.length !== 1) return;
    const filledVisitors = additionalEmployees.filter(
      (emp) => emp.lastName?.trim() || emp.firstName?.trim(),
    );
    if (filledVisitors.length !== 1) return;
    const name = getVisitorFullName(filledVisitors[0]);
    setVehicles((prev) => {
      if (prev.length !== 1) return prev;
      if (prev[0].driverName === name) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], driverName: name };
      return updated;
    });
  }, [formData.vehicleAccess, additionalEmployees, vehicles.length]);

  // === API вызовы ===
  const searchGuestsByLastName = async (lastName) => {
    try {
      const response = await axiosInstance.get(
        `${host}api/v2/find_guests/${encodeURIComponent(lastName)}/`,
      );
      const results = response.data;
      setAutocompleteResults(results);
      setShowAutocomplete(results.length > 0);
    } catch (error) {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    } finally {
      setLoadingAutocomplete(false);
    }
  };

  // === Выбор гостя ===
  const handleGuestSelect = (guest) => {
    const g = normalizeGuest(guest);
    const periodBegin = formatDate(today);
    const periodEnd = formatDate(today);

    setFormData((prev) => ({
      ...prev,
      lastName: g.lastName,
      firstName: g.firstName,
      middleName: g.middleName,
      phone: g.phone || "",
      birthDate: g.birthday ? new Date(g.birthday.split("T")[0]) : null,
      passPeriod: `${periodBegin} - ${periodEnd}`,
      vehicleAccess: false,
      organization: g.organization || "",
    }));
    setVehicles([
      {
        vehicleNumber: "",
        driverName: "",
        vehicleModel: "",
        vehicleColor: "",
        vehicleNotes: "",
      },
    ]);
    vehicleErrors.current = {};

    if (g.organization) {
      setSelectedOrganization(g.organization);
      setSelectedOrganizationId(g.organizationId || null);
      setOrgInputValue(g.organization);
      setSelectedOrgItem({ name: g.organization });
    } else {
      setOrgInputValue("");
      setSelectedOrgItem(null);
    }

    setCurrentGuestId(g.id);
    setShowAutocomplete(false);
    setAutocompleteResults([]);
    setLastNameInput("");
    lastNameRef.current?.blur();

    // Очищаем ошибки для заполненных полей
    setErrors((prev) => ({
      ...prev,
      lastName: null,
      firstName: null,
      phone: null,
      birthDate: null,
    }));
  };

  // === Поиск организаций по названию ===
  const searchOrganizations = async (query) => {
    try {
      const response = await axiosInstance.get(
        `${host}api/v2/find_organization/${encodeURIComponent(query)}/`,
      );
      const results = response.data;
      // API возвращает [{ organization: "..." }], преобразуем в { id, name }
      const mapped = results.map((item, idx) => ({
        id: idx,
        name: item.organization || "",
      }));
      setOrganizationResults(mapped);
      setShowOrganization(mapped.length > 0);
    } catch (error) {
      setOrganizationResults([]);
      setShowOrganization(false);
    } finally {
      setLoadingOrganization(false);
    }
  };

  const handleOrganizationSelect = (organization) => {
    setSelectedOrganization(organization.name);
    setSelectedOrganizationId(organization.id);
    setOrgInputValue(organization.name);
    setSelectedOrgItem({ name: organization.name });
    setFormData((prev) => ({
      ...prev,
      organization: organization.name,
    }));
    setTimeout(() => setShowOrganization(false), 100);
    setErrors((prev) => ({ ...prev, organization: null }));
  };

  // === Обработчик ввода в поле организации ===
  const handleOrgInputChange = (e) => {
    const value = e.target.value;
    setOrgInputValue(value);
    setFormData((prev) => ({ ...prev, organization: value }));

    // Если пользователь начал редактировать поле вручную — сбрасываем выбор
    if (selectedOrgItem && value !== selectedOrgItem.name) {
      setSelectedOrgItem(null);
      setSelectedOrganization("");
      setSelectedOrganizationId(null);
    }

    // Показываем список при вводе 3+ символов (только если организация ещё не выбрана)
    if (value.length >= 3 && !selectedOrgItem) {
      setShowOrganization(true);
    } else {
      setShowOrganization(false);
    }
  };

  // === Обработчики для дополнительных сотрудников ===
  const addEmployee = () => {
    setAdditionalEmployees((prev) => [
      ...prev,
      { lastName: "", firstName: "", middleName: "", comment: "" },
    ]);
  };

  const removeEmployee = (index) => {
    // Очищаем таймер и автокомплит для удаляемого сотрудника
    if (empTimerRef.current[index]) {
      clearTimeout(empTimerRef.current[index]);
      delete empTimerRef.current[index];
    }
    setEmpAutocomplete((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setAdditionalEmployees((prev) => prev.filter((_, i) => i !== index));
  };

  // === Поиск гостя для конкретного дополнительного сотрудника ===
  const searchGuestForEmployee = async (index, query) => {
    try {
      setEmpAutocomplete((prev) => ({
        ...prev,
        [index]: { ...prev[index], loading: true },
      }));
      const response = await axiosInstance.get(
        `${host}api/v2/find_guests/${encodeURIComponent(query)}/`,
      );
      const results = response.data;
      setEmpAutocomplete((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          results,
          show: results.length > 0,
          loading: false,
        },
      }));
    } catch (error) {
      setEmpAutocomplete((prev) => ({
        ...prev,
        [index]: { results: [], show: false, loading: false, query },
      }));
    }
  };

  // === Выбор гостя для дополнительного сотрудника ===
  const handleEmployeeGuestSelect = (index, guest) => {
    const g = normalizeGuest(guest);
    setAdditionalEmployees((prev) => {
      const updated = [...prev];
      updated[index] = {
        lastName: g.lastName,
        firstName: g.firstName,
        middleName: g.middleName,
        comment: prev[index]?.comment || "",
      };
      return updated;
    });
    setEmpAutocomplete((prev) => ({
      ...prev,
      [index]: {
        results: [],
        show: false,
        loading: false,
        query: g.lastName,
      },
    }));

    // Подставляем организацию, как и в основном поиске гостей
    if (g.organization) {
      setFormData((prev) => ({
        ...prev,
        organization: g.organization || "",
      }));
      setSelectedOrganization(g.organization);
      setSelectedOrganizationId(g.organizationId || null);
      setOrgInputValue(g.organization);
      setSelectedOrgItem({ name: g.organization });
    }
  };

  const handleEmployeeChange = (index, field, value) => {
    let newValue;

    if (field === "comment") {
      // Для примечания не применяем форматирование имени
      newValue = value;
    } else {
      let cleaned = value.replace(/[^а-яА-ЯёЁa-zA-Z\s\-]/g, "");
      const words = cleaned.split(/(\s+|-)/);
      const formatted = words
        .map((word) => {
          if (/^[\s\-]+$/.test(word)) return word;
          if (!word) return word;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join("");
      newValue = formatted.trim().replace(/\s+/g, " ");
    }

    setAdditionalEmployees((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: newValue };
      return updated;
    });

    // Если изменилась фамилия — запускаем дебаунс поиска
    if (field === "lastName") {
      // Сбрасываем автокомплит для короткого ввода
      if (newValue.length < 3) {
        setEmpAutocomplete((prev) => ({
          ...prev,
          [index]: {
            results: [],
            show: false,
            loading: false,
            query: newValue,
          },
        }));
        return;
      }

      setEmpAutocomplete((prev) => ({
        ...prev,
        [index]: { ...prev[index], query: newValue, loading: true },
      }));

      // Дебаунс 300мс
      if (empTimerRef.current[index]) {
        clearTimeout(empTimerRef.current[index]);
      }
      empTimerRef.current[index] = setTimeout(() => {
        searchGuestForEmployee(index, newValue);
      }, 300);
    }
  };

  // === Обработчики для машин ===
  const addVehicle = () => {
    setVehicles((prev) => [
      ...prev,
      {
        vehicleNumber: "",
        driverName: "",
        vehicleModel: "",
        vehicleColor: "",
        vehicleNotes: "",
      },
    ]);
  };

  const removeVehicle = (index) => {
    setVehicles((prev) => prev.filter((_, i) => i !== index));
    if (vehicleErrors.current[index]) {
      const next = { ...vehicleErrors.current };
      delete next[index];
      vehicleErrors.current = next;
    }
  };

  const handleVehicleChange = (index, field, value) => {
    setVehicles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    if (vehicleErrors.current[index]?.[field]) {
      const next = { ...vehicleErrors.current };
      next[index] = { ...next[index] };
      delete next[index][field];
      vehicleErrors.current = next;
    }
  };

  const validateVehicles = () => {
    const errs = {};
    let hasErrors = false;
    vehicles.forEach((v, idx) => {
      const vErr = {};
      ["vehicleNumber", "driverName"].forEach(
        (f) => {
          if (!v[f]?.trim()) {
            vErr[f] = "Это поле обязательно для заполнения";
            hasErrors = true;
          }
        },
      );
      if (Object.keys(vErr).length > 0) errs[idx] = vErr;
    });
    vehicleErrors.current = errs;
    return !hasErrors;
  };

  // === Обработчики изменений с валидацией ===
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let error = "";

    if (["lastName", "firstName", "middleName"].includes(name)) {
      let cleaned = value.replace(/[^а-яА-ЯёЁa-zA-Z\s\-]/g, "");
      const words = cleaned.split(/(\s+|-)/);
      const formatted = words
        .map((word) => {
          if (/^[\s\-]+$/.test(word)) return word;
          if (!word) return word;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join("");
      const newValue = formatted.trim().replace(/\s+/g, " ");

      error = validateName(newValue, name);
      if (error) {
        setErrors((prev) => ({ ...prev, [name]: error }));
      } else if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: null }));
      }

      if (name === "lastName") {
        setLastNameInput(newValue);
        if (newValue.length >= 3) {
          setShowAutocomplete(true);
        } else {
          setShowAutocomplete(false);
        }
      }

      setFormData((prev) => ({ ...prev, [name]: newValue }));
    } else if (name === "phone") {
      error = validatePhone(value);
      if (error) {
        setErrors((prev) => ({ ...prev, phone: error }));
      } else if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: null }));
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleDateChange = (date, field) => {
    let error = "";

    if (field === "birthDate" && date) {
      error = validateBirthDate(date);
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }

    setFormData((prev) => ({ ...prev, [field]: date }));
  };

  const handlePassPeriodChange = (type, isoDate) => {
    // Игнорируем невалидные/частичные значения при вводе с клавиатуры.
    // Парсим ISO-строку напрямую, чтобы избежать проблем с часовым поясом (UTC vs local).
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return;
    const [year, month, day] = isoDate.split("-");

    setFormData((prev) => {
      const [start, end] = prev.passPeriod.split(" - ");
      const newDate = `${day}.${month}.${year}`;

      let newStart = type === "start" ? newDate : start;
      let newEnd = type === "end" ? newDate : end;

      if (prev.passType === "single") {
        newEnd = newStart;
      }

      // Валидация
      const startDateObj = new Date(convertToISO(newStart));
      const endDateObj = new Date(convertToISO(newEnd));

      let passPeriodError = "";

      if (type === "start") {
        const startError = validatePassDate(startDateObj, true);
        if (startError) passPeriodError = startError;
      }

      if (type === "end") {
        const endError = validatePassDate(endDateObj, false, startDateObj);
        if (endError) passPeriodError = endError;
      }

      if (startDateObj > endDateObj) {
        passPeriodError =
          "Дата начала должна быть меньше или равна дате окончания";
      }

      if (passPeriodError) {
        setErrors((prev) => ({ ...prev, passPeriod: passPeriodError }));
      } else if (errors.passPeriod) {
        setErrors((prev) => ({ ...prev, passPeriod: null }));
      }

      return {
        ...prev,
        passPeriod: `${newStart} - ${newEnd}`,
      };
    });
  };

  const handleVisitTimeChange = (type, time) => {
    const [start, end] = formData.visitTime.split(" - ");
    const newStart = type === "start" ? time : start;
    const newEnd = type === "end" ? time : end;

    const startTime = newStart.replace(":", "");
    const endTime = newEnd.replace(":", "");

    if (parseInt(startTime) >= parseInt(endTime)) {
      setErrors((prev) => ({
        ...prev,
        visitTime: "Время начала должно быть меньше времени окончания",
      }));
    } else if (errors.visitTime) {
      setErrors((prev) => ({ ...prev, visitTime: null }));
    }

    setFormData((prev) => ({
      ...prev,
      visitTime: `${newStart} - ${newEnd}`,
    }));
  };

  // === Валидация формы ===
  const validateForm = () => {
    const newErrors = {};

    const requiredFields = ["visitPlace", "purpose", "organization"];

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = "Это поле обязательно для заполнения";
      }
    });

    // Валидация машин (только если включён въезд на авто)
    if (formData.vehicleAccess) {
      if (!validateVehicles()) {
        newErrors.vehicles = "Заполните данные по всем машинам";
      }
    }

    // Валидация ФИО
    const nameFields = ["lastName", "firstName"];
    nameFields.forEach((field) => {
      if (formData[field]?.trim()) {
        const nameError = validateName(formData[field], field);
        if (nameError) newErrors[field] = nameError;
      }
    });

    if (formData.middleName?.trim()) {
      const middleNameError = validateName(formData.middleName, "middleName");
      if (middleNameError) newErrors.middleName = middleNameError;
    }

    // Валидация дополнительных сотрудников
    const empErrors = {};
    additionalEmployees.forEach((emp, idx) => {
      ["lastName", "firstName"].forEach((field) => {
        if (!emp[field]?.trim()) {
          empErrors[`emp_${idx}_${field}`] =
            "Это поле обязательно для заполнения";
        } else {
          const err = validateName(emp[field], field);
          if (err) empErrors[`emp_${idx}_${field}`] = err;
        }
      });
      if (emp.middleName?.trim()) {
        const err = validateName(emp.middleName, "middleName");
        if (err) empErrors[`emp_${idx}_middleName`] = err;
      }
    });
    if (Object.keys(empErrors).length > 0) {
      newErrors.additionalEmployees = empErrors;
    }

    // Валидация телефона
    if (formData.phone?.trim()) {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) newErrors.phone = phoneError;
    }

    // Валидация даты рождения
    if (formData.birthDate) {
      const birthDateError = validateBirthDate(formData.birthDate);
      if (birthDateError) newErrors.birthDate = birthDateError;
    }

    // Валидация периода пропуска
    const [passStart, passEnd] = formData.passPeriod.split(" - ");
    const startDate = new Date(convertToISO(passStart));
    const endDate = new Date(convertToISO(passEnd));

    const startDateError = validatePassDate(startDate, true);
    if (startDateError) newErrors.passPeriod = startDateError;

    if (formData.passType !== "single") {
      const endDateError = validatePassDate(endDate, false, startDate);
      if (endDateError) newErrors.passPeriod = endDateError;
    }

    if (startDate > endDate) {
      newErrors.passPeriod =
        "Дата начала должна быть меньше или равна дате окончания";
    }

    // Валидация времени
    const [visitStart, visitEnd] = formData.visitTime.split(" - ");
    const startTime = visitStart.replace(":", "");
    const endTime = visitEnd.replace(":", "");
    if (parseInt(startTime) >= parseInt(endTime)) {
      newErrors.visitTime = "Время начала должно быть меньше времени окончания";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCloseNotification = () => {
    dispatch(clearNotification());
  };

  // === Отправка формы ===
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!validateForm()) {
      showNotification(
        dispatch,
        "Пожалуйста, заполните все поля правильно",
        "error",
      );
      return;
    }

    setLoading(true);

    const trimmedFormData = {
      ...formData,
      lastName: formData.lastName.trim(),
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim(),
      visitPlace: formData.visitPlace.trim(),
      phone: formData.phone.trim(),
      purpose: formData.purpose.trim(),
      generalNotes: formData.generalNotes.trim(),
      organization: formData.organization.trim(),
      birthDate: formData.birthDate
        ? formData.birthDate.toISOString().split("T")[0]
        : "",
    };

    // Формируем payload как в PassApplicationForm1: organizationId + organizationName отдельно
    const { organization, ...restFormData } = trimmedFormData;
    const payloadToSend = {
      ...restFormData,
      organizationId: selectedOrganizationId || null,
      organizationName: selectedOrganization || orgInputValue || null,
    };

    // Формируем массив users из списка сотрудников
    payloadToSend.users = additionalEmployees.map((emp) => ({
      lastName: emp.lastName.trim(),
      firstName: emp.firstName.trim(),
      middleName: emp.middleName.trim(),
      comment: emp.comment?.trim() || "",
    }));

    // Формируем массив vehicles (только если включён въезд на авто)
    payloadToSend.vehicles = formData.vehicleAccess
      ? vehicles.map((v) => ({
          vehicleNumber: v.vehicleNumber.trim(),
          driverName: v.driverName.trim(),
          vehicleModel: v.vehicleModel.trim(),
          vehicleColor: v.vehicleColor.trim(),
          vehicleNotes: v.vehicleNotes?.trim() || "",
        }))
      : [];

    const submitRequest = async () => {
      const response = await axiosInstance.post(
        `${host}api/v2/create_temporary_pass/`,
        payloadToSend,
      );
      return response;
    };

    try {
      let response;

      if (payloadToSend.organizationName && !selectedOrganizationId) {
        response = await submitRequest();
      } else {
        response = await submitRequest();
      }

      if (response.status >= 200 && response.status < 300) {
        showNotification(dispatch, "Заявка успешно отправлена!", "success");

        const newToday = formatDate(new Date());
        setFormData({
          lastName: "",
          firstName: "",
          middleName: "",
          visitPlace: "",
          comeTo: "",
          phone: "",
          purpose: "",
          passType: "single",
          birthDate: null,
          passPeriod: `${newToday} - ${newToday}`,
          visitTime: "07:30 - 16:00",
          weekendVisit: false,
          safetyBriefing: false,
          vehicleAccess: false,
          generalNotes: "",
          organization: "",
          owner: formData.owner,
        });
        setVehicles([
          {
            vehicleNumber: "",
            driverName: "",
            vehicleModel: "",
            vehicleColor: "",
            vehicleNotes: "",
          },
        ]);
        vehicleErrors.current = {};

        setSelectedOrganization("");
        setSelectedOrganizationId(null);
        setOrgInputValue("");
        setSelectedOrgItem(null);
        setLastNameInput("");
        // Очищаем таймеры автокомплита дополнительных сотрудников
        Object.values(empTimerRef.current).forEach((t) => clearTimeout(t));
        empTimerRef.current = {};
        setEmpAutocomplete({});
        setAdditionalEmployees([
          { lastName: "", firstName: "", middleName: "", comment: "" },
        ]);
        setErrors({});
      } else {
        throw new Error(`Неожиданный статус ответа: ${response.status}`);
      }
    } catch (error) {
      let errorMessage = "Ошибка отправки формы";

      if (error.response) {
        const { status, data } = error.response;

        if (status === 400) {
          errorMessage = data?.message || "Некорректные данные в форме";
          if (data?.errors) setErrors(data.errors);
        } else if (status === 401) {
          errorMessage = "Требуется авторизация";
        } else if (status === 403) {
          errorMessage = "У вас нет прав для создания заявки";
        } else if (status === 409) {
          errorMessage = "Заявка с такими данными уже существует";
        } else if (status === 500) {
          errorMessage = "Ошибка сервера. Попробуйте позже";
        } else {
          errorMessage = data?.message || `Ошибка сервера: ${status}`;
        }
      } else if (error.request) {
        errorMessage = "Нет ответа от сервера. Проверьте подключение";
      } else {
        errorMessage = error.message;
      }

      showNotification(dispatch, errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const [passStartDate, passEndDate] = formData.passPeriod.split(" - ");
  const startDateISO = convertToISO(passStartDate);
  const endDateISO = convertToISO(passEndDate);
  const currentDate = new Date();

  // Проверка доступа
  if (hasAccess === false) {
    return (
      <div className="tpf-container">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="tpf-access-denied">
            У вас нет прав для доступа к данному разделу
          </div>
        </motion.div>
      </div>
    );
  }

  if (formLoading) {
    return (
      <div className="tpf-container" style={{ paddingTop: 20 }}>
        <Spiner />
      </div>
    );
  }

  return (
    <div className="tpf-container">
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.12 }}
      >
        <div className="tpf-alert">
          <strong>Внимание:</strong> Для оформления заявки на пропуск необходимо
          заполнить все обязательные поля, отмеченные *
        </div>

        <form onSubmit={handleSubmit}>
          {/* Основная информация */}
          <div className="tpf-section">
            <h4 className="tpf-section-title">Основная информация</h4>

            <div className="tpf-field">
              <label className="tpf-label">Сотрудник(и)</label>
              <div>
                {additionalEmployees.length > 0 && (
                  <div className="tpf-employee-list">
                    {additionalEmployees.map((emp, idx) => {
                      const ac = empAutocomplete[idx] || {};
                      return (
                        <div key={idx} className="tpf-employee-row">
                          <div className="tpf-employee-fields">
                            <div className="tpf-employee-field">
                              <input
                                className="tpf-input"
                                type="text"
                                placeholder="Фамилия"
                                value={emp.lastName}
                                onChange={(e) =>
                                  handleEmployeeChange(
                                    idx,
                                    "lastName",
                                    e.target.value,
                                  )
                                }
                                onBlur={() =>
                                  setTimeout(() => {
                                    setEmpAutocomplete((prev) => ({
                                      ...prev,
                                      [idx]: { ...prev[idx], show: false },
                                    }));
                                  }, 200)
                                }
                                maxLength={100}
                                autoComplete="off"
                              />

                              {/* Автодополнение гостей для этого сотрудника */}
                              {ac.show && (
                                <div className="tpf-ac">
                                  {ac.loading ? (
                                    <div className="tpf-ac-loading">
                                      <span className="tpf-spinner-ac" /> Поиск
                                      гостей
                                    </div>
                                  ) : ac.results?.length === 0 ? (
                                    <div className="tpf-ac-empty">
                                      По вашему запросу ничего не найдено
                                    </div>
                                  ) : (
                                    <div className="tpf-ac-table-wrap">
                                      <table className="tpf-ac-table">
                                        <thead>
                                          <tr>
                                            <th>ФИО</th>
                                            <th>Дата рождения</th>
                                            <th>Организация</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {ac.results?.map((item, ri) => (
                                            <tr
                                              key={`emp-${idx}-${item.id}-${ri}`}
                                              onClick={() =>
                                                handleEmployeeGuestSelect(
                                                  idx,
                                                  item,
                                                )
                                              }
                                              className="tpf-ac-row"
                                            >
                                              <td>
                                                <strong>{item.NAME}</strong>
                                              </td>
                                              <td>
                                                {item.BIRTHDAY
                                                  ? formatDate(
                                                      new Date(item.BIRTHDAY),
                                                    )
                                                  : "—"}
                                              </td>
                                              <td>
                                                {item.ORGANIZATION || "—"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <input
                              className="tpf-input"
                              type="text"
                              placeholder="Имя"
                              value={emp.firstName}
                              onChange={(e) =>
                                handleEmployeeChange(
                                  idx,
                                  "firstName",
                                  e.target.value,
                                )
                              }
                              maxLength={100}
                            />
                            <input
                              className="tpf-input"
                              type="text"
                              placeholder="Отчество"
                              value={emp.middleName}
                              onChange={(e) =>
                                handleEmployeeChange(
                                  idx,
                                  "middleName",
                                  e.target.value,
                                )
                              }
                              maxLength={100}
                            />
                          </div>
                          {/* Примечание */}
                          <textarea
                            className="tpf-input tpf-textarea"
                            rows={2}
                            placeholder="Примечание (необязательно)"
                            value={emp.comment || ""}
                            onChange={(e) =>
                              handleEmployeeChange(
                                idx,
                                "comment",
                                e.target.value,
                              )
                            }
                          ></textarea>
                          <button
                            type="button"
                            className="tpf-emp-remove"
                            onClick={() => removeEmployee(idx)}
                            title="Удалить сотрудника"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  type="button"
                  className="tpf-emp-add"
                  onClick={addEmployee}
                >
                  + Добавить сотрудника
                </button>
              </div>
            </div>

            {/* Место посещения */}
            <div className="tpf-field">
              <label className="tpf-label">
                Место посещения <span className="required">*</span>
              </label>
              <select
                className="tpf-select"
                name="visitPlace"
                value={formData.visitPlace}
                onChange={handleChange}
              >
                <option value="">Выберите место</option>
                {visitPlaces.map((item) => (
                  <option key={item.ID} value={item.NAME}>
                    {item.NAME}
                  </option>
                ))}
              </select>
              <div className="tpf-error">{errors.visitPlace}</div>
            </div>

            {/* Организация */}
            <div className="tpf-field">
              <label className="tpf-label">
                Организация <span className="required">*</span>
              </label>
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "stretch",
                  }}
                >
                  <input
                    className="tpf-input"
                    ref={organizationRef}
                    type="text"
                    value={orgInputValue}
                    onChange={handleOrgInputChange}
                    onBlur={() =>
                      setTimeout(() => setShowOrganization(false), 200)
                    }
                    placeholder="Введите организацию (3+ символа)"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
                <div className="tpf-error">{errors.organization}</div>

                {showOrganization && (
                  <div className="tpf-ac">
                    {loadingOrganization ? (
                      <div className="text-center p-3">
                        <span className="tpf-spinner-ac" />
                        <p className="mt-2 mb-0 small">Поиск организаций...</p>
                      </div>
                    ) : organizationResults.length === 0 ? (
                      <div className="tpf-ac-empty">
                        Организация не найдена. Попробуйте другой запрос.
                      </div>
                    ) : (
                      organizationResults.map((org) => (
                        <div
                          key={org.id}
                          className="tpf-ac-item"
                          onClick={() => handleOrganizationSelect(org)}
                        >
                          {org.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Телефон */}
            {/* Телефон закомментирован */}

            {/* Цель посещения */}
            <div className="tpf-field">
              <label className="tpf-label">
                Цель посещения <span className="required">*</span>
              </label>
              <select
                className="tpf-select"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
              >
                <option value="">Выберите цель</option>
                {visitPurposes.map((item) => (
                  <option key={item.ID} value={item.NAME}>
                    {item.NAME}
                  </option>
                ))}
              </select>
              <div className="tpf-error">{errors.purpose}</div>
            </div>
          </div>

          {/* Детали пропуска */}
          <div className="tpf-section">
            <h4 className="tpf-section-title">Детали пропуска</h4>

            <div className="tpf-row">
              <div className="tpf-field">
                <label className="tpf-label">Тип пропуска</label>
                <select
                  className="tpf-select"
                  name="passType"
                  value={formData.passType}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value === "temporary") {
                      setFormData((prev) => ({ ...prev, safetyBriefing: true }));
                    }
                  }}
                >
                  <option value="single">Разовый</option>
                  <option value="temporary">Временный</option>
                </select>
              </div>

              {/* <div className="tpf-field"> */}
              {/* Дата рождения закомментирована */}
              {/* </div> */}
            </div>

            <div className="tpf-field">
              <label className="tpf-label">Дата/период действия пропуска</label>
              <div className="tpf-row">
                <div className="tpf-field">
                  <div
                    className="tpf-hint"
                    style={{ fontSize: "0.8rem", marginBottom: 4 }}
                  >
                    Начало
                  </div>
                  <input
                    className="tpf-input"
                    type="date"
                    value={startDateISO}
                    onChange={(e) =>
                      handlePassPeriodChange("start", e.target.value)
                    }
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                {formData.passType !== "single" && (
                  <div className="tpf-field">
                    <div
                      className="tpf-hint"
                      style={{ fontSize: "0.8rem", marginBottom: 4 }}
                    >
                      Окончание
                    </div>
                    <input
                      className="tpf-input"
                      type="date"
                      value={endDateISO}
                      onChange={(e) =>
                        handlePassPeriodChange("end", e.target.value)
                      }
                      min={startDateISO}
                    />
                  </div>
                )}
              </div>
              <div className="tpf-hint">Период: {formData.passPeriod}</div>
              {errors.passPeriod && (
                <div className="tpf-error">{errors.passPeriod}</div>
              )}
            </div>

            <div className="tpf-field">
              <label className="tpf-label">Разрешенное время визита</label>
              <div className="tpf-row">
                <div className="tpf-field">
                  <div
                    className="tpf-hint"
                    style={{ fontSize: "0.8rem", marginBottom: 4 }}
                  >
                    С
                  </div>
                  <input
                    className="tpf-input"
                    type="time"
                    value={formData.visitTime.split(" - ")[0]}
                    onChange={(e) =>
                      handleVisitTimeChange("start", e.target.value)
                    }
                  />
                </div>
                <div className="tpf-field">
                  <div
                    className="tpf-hint"
                    style={{ fontSize: "0.8rem", marginBottom: 4 }}
                  >
                    По
                  </div>
                  <input
                    className="tpf-input"
                    type="time"
                    value={formData.visitTime.split(" - ")[1]}
                    onChange={(e) =>
                      handleVisitTimeChange("end", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="tpf-hint">Время: {formData.visitTime}</div>
              {errors.visitTime && (
                <div className="tpf-error">{errors.visitTime}</div>
              )}
            </div>
          </div>

          {/* Дополнительные параметры */}
          <div className="tpf-section">
            <h4 className="tpf-section-title">Дополнительные параметры</h4>

            <div className="tpf-field">
              <label
                className={`tpf-checkbox ${formData.weekendVisit ? "checked" : ""}`}
              >
                <span className="tpf-checkbox-box">
                  <span className="tpf-checkbox-check">✓</span>
                </span>
                <span className="tpf-checkbox-label">
                  Необходимость визита в выходные и праздничные дни
                </span>
                <input
                  type="checkbox"
                  name="weekendVisit"
                  checked={formData.weekendVisit}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
              </label>
              <label
                className={`tpf-checkbox ${formData.safetyBriefing ? "checked" : ""} ${formData.passType === "temporary" ? "tpf-checkbox--locked" : ""}`}
              >
                <span className="tpf-checkbox-box">
                  <span className="tpf-checkbox-check">✓</span>
                </span>
                <span className="tpf-checkbox-label">
                  Необходимость проведения инструктажа по ОТ и ТБ
                </span>
                <input
                  type="checkbox"
                  name="safetyBriefing"
                  checked={formData.safetyBriefing}
                  onChange={(e) => {
                    // Для временного пропуска нельзя снимать галочку инструктажа
                    if (formData.passType === "temporary" && !e.target.checked) {
                      return;
                    }
                    handleChange(e);
                  }}
                  disabled={formData.passType === "temporary"}
                  style={{ display: "none" }}
                />
              </label>
              <label
                className={`tpf-checkbox ${formData.vehicleAccess ? "checked" : ""}`}
              >
                <span className="tpf-checkbox-box">
                  <span className="tpf-checkbox-check">✓</span>
                </span>
                <span className="tpf-checkbox-label">
                  Необходимость въезда на АВТО
                </span>
                <input
                  type="checkbox"
                  name="vehicleAccess"
                  checked={formData.vehicleAccess}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          </div>

          {/* Данные автомобиля */}
          {formData.vehicleAccess && (
            <div className="tpf-section tpf-section--vehicle">
              <h4 className="tpf-section-title">
                Данные автомобиля(ей) <span className="required">*</span>
              </h4>

              <div className="tpf-employee-list">
                {vehicles.map((v, idx) => {
                  const vErr = vehicleErrors.current[idx] || {};
                  return (
                    <div key={idx} className="tpf-employee-row">
                      <div className="tpf-vehicle-fields">
                        <div className="tpf-field">
                          <label className="tpf-label">
                            Номер <span className="required">*</span>
                          </label>
                          <input
                            className="tpf-input"
                            type="text"
                            value={v.vehicleNumber}
                            onChange={(e) =>
                              handleVehicleChange(
                                idx,
                                "vehicleNumber",
                                e.target.value,
                              )
                            }
                            placeholder="AB 1234 CD"
                          />
                          <div className="tpf-error">
                            {vErr.vehicleNumber}
                          </div>
                        </div>
                        <div className="tpf-field">
                          <label className="tpf-label">
                            Водитель <span className="required">*</span>
                          </label>
                          <select
                            className="tpf-input"
                            value={v.driverName || ""}
                            onChange={(e) =>
                              handleVehicleChange(
                                idx,
                                "driverName",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">—</option>
                            {additionalEmployees
                              .filter(
                                (emp) =>
                                  emp.lastName?.trim() || emp.firstName?.trim(),
                              )
                              .map((emp, eIdx) => {
                                const name = getVisitorFullName(emp);
                                return (
                                  <option key={eIdx} value={name}>
                                    {name}
                                  </option>
                                );
                              })}
                          </select>
                          <div className="tpf-error">{vErr.driverName}</div>
                        </div>
                        <div className="tpf-field">
                          <label className="tpf-label">
                            Модель
                          </label>
                          <input
                            className="tpf-input"
                            type="text"
                            value={v.vehicleModel}
                            onChange={(e) =>
                              handleVehicleChange(
                                idx,
                                "vehicleModel",
                                e.target.value,
                              )
                            }
                            placeholder="Toyota Camry"
                          />
                          <div className="tpf-error">{vErr.vehicleModel}</div>
                        </div>
                        <div className="tpf-field">
                          <label className="tpf-label">
                            Цвет
                          </label>
                          <input
                            className="tpf-input"
                            type="text"
                            value={v.vehicleColor}
                            onChange={(e) =>
                              handleVehicleChange(
                                idx,
                                "vehicleColor",
                                e.target.value,
                              )
                            }
                            placeholder="Черный"
                          />
                          <div className="tpf-error">{vErr.vehicleColor}</div>
                        </div>
                      </div>

                      <div className="tpf-field">
                        <label className="tpf-label">Примечание</label>
                        <textarea
                          className="tpf-input tpf-textarea"
                          rows={2}
                          value={v.vehicleNotes || ""}
                          onChange={(e) =>
                            handleVehicleChange(
                              idx,
                              "vehicleNotes",
                              e.target.value,
                            )
                          }
                          placeholder="Дополнительная информация об автомобиле"
                        ></textarea>
                      </div>

                      <button
                        type="button"
                        className="tpf-emp-remove"
                        onClick={() => removeVehicle(idx)}
                        title="Удалить машину"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="tpf-emp-add"
                onClick={addVehicle}
              >
                + Добавить машину
              </button>

              {errors.vehicles && (
                <div className="tpf-error" style={{ marginTop: 8 }}>
                  {errors.vehicles}
                </div>
              )}
            </div>
          )}

          {/* Общее примечание */}
          <div className="tpf-section">
            <div className="tpf-field">
              <label className="tpf-label">Общее примечание</label>
              <textarea
                className="tpf-input tpf-textarea"
                rows={3}
                name="generalNotes"
                value={formData.generalNotes}
                onChange={handleChange}
                placeholder="Дополнительная информация к заявке"
              ></textarea>
            </div>
          </div>

          {/* Кнопка отправки */}
          <div className="tpf-submit-wrap">
            {loading ? (
              <Button type="submit" disabled>
                <span className="tpf-spinner-ac" />
                Отправка...
              </Button>
            ) : (
              <Button type="submit">Отправить заявку</Button>
            )}
          </div>
        </form>
      </motion.div>
      {/* Уведомления */}
      {notification.show && (
        <ToastNotification
          show={notification.show}
          message={notification.message}
          onClose={handleCloseNotification}
          type={notification.type}
        />
      )}

      {/* Дополнительные стили */}
    </div>
  );
};

export default PassApplicationForm;
